import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// The "AI review" tab of Social Moderation — every Connect post, edit, repost and
// comment the AI checked against the Connect content policy, with the ones that need a
// human (held, couldn't be checked, flagged in monitor mode) as the default view.
//
// ── ITS OWN TABLE, NOT MORE COLUMNS ON THE POSTS ONE ────────────────────────
//
// The DM precedent on the same page: the posts table is already a nest of
// `browsingAll ? … : …` switches, and an AI check shares almost none of its columns. A
// check is not always a post — a blocked attempt was never saved, a comment is not a
// post, an edit is a second check on an existing post — so the unit here is the REVIEW
// ROW, keyed by its own id, and it opens its own modal (AiReviewModal).
//
// ── WHAT THE PAGE OWNS AND WHAT THIS OWNS ───────────────────────────────────
//
// The page keeps the controls that live in the scaffold — date range, search box, the
// filter pills (exported below so they render in the filters row, like Reported posts'
// status pills) and the page/size state. This component owns the fetch, the table, the
// pager and the modal. Counts are lifted back up through `onCounts` so the pills can
// carry them. A 403 is the same "not a moderator here" as the other tabs and goes back
// to the page for its full-page AccessDeniedPanel.
//
// Server-paged and server-ordered (newest first), so the headers are plain, exactly as
// on All posts — a sortable header would reorder one page and present it as the whole.
//
// NO CSV EXPORT, deliberately. A blocked attempt is by definition the content that must
// not circulate — a customer's phone number, an employee's payslip — and a spreadsheet
// of it on a shared drive would be the leak the block prevented. The page hides its
// Download button on this tab for that reason.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Paperclip } from 'lucide-react';
import { BlockLoader } from '../loading';
import PaginationFooter from '../PaginationFooter';
import AiReviewModal, {
  AI_TARGET_CLS,
  AI_TARGET_LABEL,
  AiDecisionChip,
  ITEM_STATE_LABEL,
  ModeratorStatusChip,
} from './AiReviewModal';
import {
  connectPolicyAPI,
  connectPolicyErrorMessage,
  connectPolicyErrorStatus,
  parseServerTimestamp,
  type AiReviewCounts,
  type AiReviewFilter,
  type AiReviewRow,
} from '../../services/connectPolicyApi';

/** Declaration order IS render order. "Needs review" first because it is the worklist;
 *  "All checks" last because it is mostly allowed posts — the audit trail, not work. */
export const AI_REVIEW_FILTERS: { value: AiReviewFilter; label: string; count: keyof AiReviewCounts; title: string }[] = [
  { value: 'needs_review', label: 'Needs review', count: 'needsReview', title: 'Held posts, posts the AI could not check, and monitor-mode flags still waiting for a moderator — from any date, not just the range shown' },
  { value: 'held', label: 'Held', count: 'held', title: 'Saved but visible only to the author until a moderator decides' },
  { value: 'blocked', label: 'Blocked', count: 'blocked', title: 'Never posted — the author was told which rule and how to fix it' },
  { value: 'unchecked', label: 'Not checked', count: 'unchecked', title: 'The AI was unavailable, so these were published without a check' },
  { value: 'monitor', label: 'Monitor flags', count: 'monitorFlagged', title: 'Published in monitor mode, but the AI would have held or blocked them' },
  { value: 'all', label: 'All checks', count: 'checked', title: 'Every check in the date range, including allowed posts' },
];

/** The pills, rendered by the page in the scaffold's filters row. Counts are for the
 *  project and date range and ignore the pill and the search, so they read as "how much
 *  is in each bucket" rather than changing as you click between them. */
export function AiReviewFilterPills({ value, onChange, counts }: {
  value: AiReviewFilter;
  onChange: (next: AiReviewFilter) => void;
  counts: AiReviewCounts | null;
}) {
  return (
    // A row of its own, first in the scaffold's filters row (order-first + basis-full):
    // the six pills are wide enough that, sharing a line with the search box and the
    // date range, they pushed the end date onto the next line apart from the start date.
    <div className="order-first basis-full">
      <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-0.5">
        {AI_REVIEW_FILTERS.map((f) => {
          const n = counts ? Number(counts[f.count] ?? 0) : null;
          const active = value === f.value;
          return (
            <button
              key={f.value}
              type="button"
              title={f.title}
              onClick={() => onChange(f.value)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                active ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
              {n != null && (
                <span className={`rounded-full px-1.5 text-[11px] font-semibold tabular-nums ${
                  active
                    ? 'bg-white/20 text-white'
                    // The worklist count is the one number that asks for attention.
                    : f.value === 'needs_review' && n > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Column widths are set so the table fits the 1180px card at a 1280px viewport with
// the Actions column in view: every column but Content has a width, and Content (line-
// clamped to two lines) takes what is left. Narrower screens scroll sideways, with
// Actions pinned to the right edge (the same sticky pattern as the other admin tables).
const TH_TEXT = 'whitespace-nowrap py-3 text-xs font-medium uppercase tracking-wider text-gray-500';
const TH_CLS = `${TH_TEXT} px-4 text-left`;
const TH_ACTIONS_CLS = `${TH_TEXT} sticky right-0 z-10 bg-gray-100 px-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]`;

const PAGE_SIZE_OPTIONS = [
  { label: '25 / page', value: 25 },
  { label: '50 / page', value: 50 },
  { label: '100 / page', value: 100 },
];

// parseServerTimestamp, not `new Date`: raw Postgres TIMESTAMPTZ text, which Safari
// will not parse.
// Day and time on two lines, so the Time column stays narrow.
const fmtDateTime = (iso: string | null | undefined): { day: string; time: string } | null => {
  const d = parseServerTimestamp(iso);
  return !d
    ? null
    : {
        day: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }),
        time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }),
      };
};

/** Distinct rule names, in the order the AI cited them. */
const ruleNames = (r: AiReviewRow): string[] =>
  Array.from(new Set((r.violations ?? []).map((v) => v.ruleName || v.ruleId).filter(Boolean)));

interface Props {
  projectId: number;
  filter: AiReviewFilter;
  /** Applied date range, YYYY-MM-DD. */
  from: string;
  to: string;
  /** Already debounced by the page. Sent as `q`: a server search, because this view
   *  is paged and a browser-side filter would only ever see one page. */
  search: string;
  page: number;
  pageSize: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: (n: number) => void;
  onCounts: (counts: AiReviewCounts | null) => void;
  onForbidden: () => void;
}

export default function AiReviewTab({
  projectId, filter, from, to, search, page, pageSize, setPage, setPageSize, onCounts, onForbidden,
}: Props) {
  const [rows, setRows] = useState<AiReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<AiReviewRow['id'] | null>(null);
  const reqRef = useRef(0);

  const fetchRows = useCallback(() => {
    // Same stale-response guard as the page: a filter click mid-flight bumps the
    // counter, so the earlier answer is dropped before it can paint the wrong bucket.
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    connectPolicyAPI
      .listAiReviews(projectId, {
        filter, from, to,
        ...(search ? { q: search } : {}),
        limit: pageSize, offset: (page - 1) * pageSize,
      })
      .then((data) => {
        if (reqId !== reqRef.current) return;
        setRows(data.rows ?? []);
        setTotal(Number(data.total ?? 0));
        onCounts(data.counts ?? null);
      })
      .catch((err: unknown) => {
        if (reqId !== reqRef.current) return;
        setRows([]);
        setTotal(0);
        onCounts(null);
        if (connectPolicyErrorStatus(err) === 403) {
          onForbidden();
          return;
        }
        setError(connectPolicyErrorMessage(err, 'Failed to load the AI review list'));
      })
      .finally(() => { if (reqId === reqRef.current) setLoading(false); });
    // onCounts / onForbidden are state setters wrapped by the page and deliberately
    // left out: a new function identity must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filter, from, to, search, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  // The page number is a REQUEST input here, so a shrinking total (a decision just
  // emptied the last page) must pull it back, or the table sits empty on page 3 of 2.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  return (
    <>
      {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading && rows.length === 0 ? (
        <BlockLoader className="h-60" />
      ) : (
        <>
          <div className="mb-2 mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span>
              {total} check{total === 1 ? '' : 's'}{search ? ' matching your search' : ''}
              {loading && <span className="ml-2 text-gray-400">Refreshing…</span>}
            </span>
            <Link to="/governance/connect-content-policy" className="font-medium text-primary-600 hover:underline">
              Connect content policy
            </Link>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className={`${TH_CLS} w-24`}>Time</th>
                  <th className={`${TH_CLS} w-36`}>Author</th>
                  <th className={`${TH_TEXT} w-24 px-3 text-left`}>Type</th>
                  <th className={TH_CLS}>Content</th>
                  <th className={`${TH_TEXT} w-20 px-3 text-right`}>Photos</th>
                  <th className={`${TH_TEXT} w-28 px-3 text-left`}>AI decision</th>
                  <th className={`${TH_CLS} w-40`}>Rules</th>
                  <th className={`${TH_CLS} w-32`}>Status</th>
                  <th className={TH_ACTIONS_CLS}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-sm text-gray-400">
                    {filter === 'needs_review' && !search
                      // The healthy state, named as such — plus where the settings are,
                      // since "nothing here" is also what a policy switched Off looks like.
                      ? 'Nothing needs review. Held posts, posts the AI couldn’t check and monitor-mode flags land here, whatever their date.'
                      : 'No AI checks match these filters. Try widening the date range or clearing the search.'}
                  </td></tr>
                ) : rows.map((r) => {
                  const names = ruleNames(r);
                  const when = fmtDateTime(r.createdAt);
                  return (
                    <tr
                      key={String(r.id)}
                      onClick={() => setOpenId(r.id)}
                      className="group cursor-pointer align-top hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                        {when ? (
                          <>
                            <div>{when.day}</div>
                            <div className="text-xs text-gray-400">{when.time}</div>
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.author?.name || r.author?.empCode}</div>
                        <div className="font-mono text-xs text-gray-400">{r.author?.empCode}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${AI_TARGET_CLS[r.targetType] ?? 'bg-gray-100 text-gray-600'}`}>
                          {AI_TARGET_LABEL[r.targetType] ?? r.targetType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="line-clamp-2 min-w-[14rem] break-words" title={r.contentPreview ?? undefined}>
                          {r.contentPreview || <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right text-gray-600">
                        {r.mediaCount > 0 ? (
                          <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> ×{r.mediaCount}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-3"><AiDecisionChip decision={r.decision} mode={r.mode} /></td>
                      <td className="px-4 py-3 text-gray-700">
                        {names.length > 0 ? (
                          <div className="line-clamp-3 break-words" title={names.join(', ')}>{names.join(', ')}</div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <ModeratorStatusChip status={r.moderatorStatus} outcome={r.outcome} />
                        <div className="mt-0.5 text-[11px] leading-snug text-gray-400">
                          {ITEM_STATE_LABEL[r.itemState] ?? r.itemState}
                        </div>
                      </td>
                      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50"><TableActionGroup>
                        <TableActionButton action="review" label="Review" type="button" onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }} />
                      </TableActionGroup></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <PaginationFooter
              safePage={safePage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              totalRows={total}
              // Back to page 1 is load-bearing, not cosmetic: offset = page × size.
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          )}
        </>
      )}

      {/* Portalled: the scaffold clips overflow, so a modal rendered inside it would
          scroll with the table. Keyed so opening another row starts clean. */}
      {openId != null && createPortal(
        <AiReviewModal
          key={String(openId)}
          reviewId={openId}
          projectId={projectId}
          onClose={() => setOpenId(null)}
          onChanged={fetchRows}
        />,
        document.body,
      )}
    </>
  );
}
